import { formatEpoch } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@voicely/ui-mui/feedback/Dialog";
import { Button } from "@voicely/ui-mui/form/Button";
import { Checkbox } from "@voicely/ui-mui/form/Checkbox";
import { FormControlLabel } from "@voicely/ui-mui/form/FormControl";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { TEAM_MEMBER_ROLE_ENUM, type TeamMemberRole } from "@voicely/zod-schemas/enums.zod";
import { PASSWORD_MIN_LENGTH } from "@voicely/zod-schemas/team_app.zod";
import { useState } from "react";
import { toast } from "react-toastify";

interface Props {
	teamId: string;
	teamName: string;
	onClose: () => void;
}

/** A password the server generated, shown until the operator dismisses it. */
interface IssuedPassword {
	email: string;
	password: string;
}

/**
 * Who is in a workspace, and how they sign in.
 *
 * Open state is in the URL (`?panel=members:<teamId>`, held by the page), so a
 * refresh and a pasted link both land back here. What is typed into the form is
 * not: a password in a URL would sit in history and logs.
 *
 * A generated password is shown ONCE and kept only in this component's memory.
 * The server stores a hash and cannot show it again, which is why it is drawn
 * as loudly as it is — closing the dialog is the only way it goes away, and a
 * lost one is a reset, not a lookup.
 */
export const TeamMembersDialog = ({ teamId, teamName, onClose }: Props) => {
	const queryClient = useQueryClient();
	const membersQuery = useQuery(
		orpc.superAdmin.listTeamMembers.queryOptions({ input: { teamId } }),
	);

	const [email, setEmail] = useState("");
	const [role, setRole] = useState<TeamMemberRole>("Member");
	const [createLogin, setCreateLogin] = useState(true);
	const [password, setPassword] = useState("");
	const [issued, setIssued] = useState<IssuedPassword | null>(null);
	const [resetTarget, setResetTarget] = useState<string | null>(null);
	const [resetPassword, setResetPassword] = useState("");

	const refresh = async () => {
		await queryClient.invalidateQueries({
			queryKey: orpc.superAdmin.listTeamMembers.key({ input: { teamId } }),
		});
	};

	const addMember = useMutation(
		orpc.teams.adminAddMember.mutationOptions({
			onSuccess: async (result) => {
				toast.success(
					result.accountCreated
						? `${result.member.email} added, with a new password login.`
						: `${result.member.email} added.`,
				);
				if (result.generatedPassword && result.member.email) {
					setIssued({ email: result.member.email, password: result.generatedPassword });
				}
				setEmail("");
				setPassword("");
				await refresh();
			},
		}),
	);

	const reset = useMutation(
		orpc.teams.adminResetPassword.mutationOptions({
			onSuccess: async (result) => {
				toast.success(
					result.sessionsEnded > 0
						? `Password reset. ${result.sessionsEnded} signed-in session(s) ended.`
						: "Password reset.",
				);
				if (result.generatedPassword) {
					setIssued({ email: result.email, password: result.generatedPassword });
				}
				setResetTarget(null);
				setResetPassword("");
				await refresh();
			},
		}),
	);

	const passwordTooShort = password !== "" && password.length < PASSWORD_MIN_LENGTH;
	const resetTooShort = resetPassword !== "" && resetPassword.length < PASSWORD_MIN_LENGTH;
	const error = (addMember.error ?? reset.error) as { message?: string } | null;

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>Members of {teamName}</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ pt: 1 }}>
					{issued && (
						<Alert severity="warning" onClose={() => setIssued(null)}>
							Password for <strong>{issued.email}</strong>:{" "}
							<code style={{ userSelect: "all" }}>{issued.password}</code>
							<br />
							Shown once. Send it to them over a private channel; it cannot be looked up again, only
							reset.
						</Alert>
					)}

					{error?.message && <Alert severity="error">{error.message}</Alert>}

					{membersQuery.isPending ? (
						<LoadingSpinner />
					) : membersQuery.error ? (
						<Alert severity="error">{membersQuery.error.message}</Alert>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Email</TableCell>
										<TableCell>Role</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Sign-in</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{membersQuery.data.length === 0 && (
										<TableRow>
											<TableCell colSpan={5}>
												<Typography color="text.secondary">No members.</Typography>
											</TableCell>
										</TableRow>
									)}
									{membersQuery.data.map((member) => (
										<TableRow key={member.id}>
											<TableCell>{member.email ?? member.phoneNumber ?? "—"}</TableCell>
											<TableCell>{member.role}</TableCell>
											<TableCell>
												{member.userId ? (
													<Chip
														size="small"
														color="success"
														label={`joined ${member.joinedAt ? formatEpoch(member.joinedAt) : ""}`.trim()}
													/>
												) : (
													<Chip size="small" label="invited, no account yet" />
												)}
											</TableCell>
											<TableCell>
												<Chip
													size="small"
													variant="outlined"
													label={member.hasPasswordLogin ? "password" : "Google"}
												/>
											</TableCell>
											<TableCell align="right">
												{member.email && member.userId && (
													<Button size="small" onClick={() => setResetTarget(member.email)}>
														Reset password
													</Button>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					{resetTarget && (
						<Stack
							component="form"
							spacing={1.5}
							onSubmit={(event) => {
								event.preventDefault();
								reset.mutate({ email: resetTarget, password: resetPassword || undefined });
							}}
						>
							<Typography variant="subtitle2">Reset password for {resetTarget}</Typography>
							<TextField
								size="small"
								label="New password"
								helperText={
									resetTooShort
										? `At least ${PASSWORD_MIN_LENGTH} characters`
										: "Leave blank to generate one. Ends every session this person has open."
								}
								error={resetTooShort}
								value={resetPassword}
								onChange={(e) => setResetPassword(e.target.value)}
								autoComplete="off"
							/>
							<Stack direction="row" spacing={1}>
								<Button
									type="submit"
									variant="contained"
									color="warning"
									disabled={reset.isPending || resetTooShort}
								>
									Reset password
								</Button>
								<Button onClick={() => setResetTarget(null)}>Cancel</Button>
							</Stack>
						</Stack>
					)}

					<Stack
						component="form"
						spacing={1.5}
						onSubmit={(event) => {
							event.preventDefault();
							addMember.mutate({
								teamId,
								email: email.trim(),
								role,
								createPasswordLogin: createLogin,
								password: createLogin && password ? password : undefined,
							});
						}}
					>
						<Typography variant="subtitle2">Add an email to this workspace</Typography>
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
							<TextField
								size="small"
								type="email"
								label="Email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								sx={{ flex: 1 }}
								required
							/>
							<TextField
								size="small"
								select
								label="Role"
								value={role}
								onChange={(e) => setRole(e.target.value as TeamMemberRole)}
								sx={{ minWidth: 130 }}
							>
								{TEAM_MEMBER_ROLE_ENUM.map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</TextField>
						</Stack>
						<FormControlLabel
							control={
								<Checkbox
									checked={createLogin}
									onChange={(e) => setCreateLogin(e.target.checked)}
								/>
							}
							label="Create an email + password login if this address has no account"
						/>
						{createLogin && (
							<TextField
								size="small"
								label="Initial password"
								helperText={
									passwordTooShort
										? `At least ${PASSWORD_MIN_LENGTH} characters`
										: "Leave blank to generate one. Ignored when the address already has an account."
								}
								error={passwordTooShort}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								autoComplete="off"
							/>
						)}
						<div>
							<Button
								type="submit"
								variant="contained"
								disabled={addMember.isPending || email.trim() === "" || passwordTooShort}
							>
								Add member
							</Button>
						</div>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
